import React, { useState, useEffect, useRef } from 'react';
import {
  Pane,
  majorScale,
  NotificationsIcon,
  IconButton,
  Popover,
  Position,
  Pill,
  Switch,
  minorScale,
  EyeOpenIcon,
  Tooltip,
  Text,
  Heading,
  toaster,
  Button,
} from 'evergreen-ui';
import PropTypes from 'prop-types';
import { io } from 'socket.io-client';
import { api } from '../../services/api';
import inbox from '../../assets/images/inbox.png';

const NotificationsMenu = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [showOnlyUnread, setShowOnlyUnread] = useState(true);
  const [totalUnread, setTotalUnread] = useState(0);
  const socket = useRef();

  let unmounted = false;
  const fetchData = async (append = false) => {
    setLoading(true);
    try {
      const { data } = await api.notifications.list(
        showOnlyUnread,
        append ? metadata.page + 1 : 1,
      );
      // Get unread notifications counter
      const unread = await api.notifications.list(true, 1);
      if (!unmounted) {
        setNotifications(append ? notifications.concat(data.data) : data.data);
        setMetadata(data.metadata);
        setTotalUnread(unread.data.metadata.item_count);
        setLoading(false);
      }
    } catch (error) {
      toaster.danger('There was an error while proccessing your request');
      setLoading(false);
    }
  };

  const handleNewNotification = (n) => {
    fetchData();
    toaster.notify(n.title, { description: n.description });
  };

  useEffect(() => {
    fetchData();
  }, [showOnlyUnread]);

  useEffect(() => {
    socket.current = io({ path: '/api/ws/socket.io' });

    socket.current.on('new_notification', (e) => handleNewNotification(e));

    return () => {
      unmounted = true;
      socket.current.close();
    };
  }, []);

  const markAllAsRead = () => {
    socket.current.emit('mark_all_as_read');
    const newNotifications = [];
    notifications.forEach((n) => {
      const notif = { ...n, seen: true };
      newNotifications.push(notif);
    });
    setNotifications(newNotifications);
    setTotalUnread(0);
  };

  const handleNextPage = (e) => {
    e.stopPropagation();
    fetchData(true);
  };

  return (
    <Pane display="flex" alignItems="center" marginRight={majorScale(2)}>
      <Popover
        position={Position.BOTTOM_RIGHT}
        content={(
          <Pane maxHeight="50vh" minWidth="360px" overflowY="auto">
            <Pane display="flex" justifyContent="space-between" alignItems="center" padding={majorScale(1)} borderBottom="1px solid #EDEFF5">
              <Pane display="flex" alignItems="center">
                <Switch
                  checked={showOnlyUnread}
                  disabled={loading}
                  onChange={(e) => setShowOnlyUnread(e.target.checked)} />
                <Text marginLeft={minorScale(2)}>Unread only</Text>
              </Pane>
              <Pane>
                <Tooltip content="Mark all as read">
                  <IconButton
                    icon={EyeOpenIcon}
                    size="small"
                    disabled={loading || notifications.length < 1}
                    marginRight={minorScale(2)}
                    onClick={markAllAsRead} />
                </Tooltip>
              </Pane>
            </Pane>

            {notifications.length < 1 && (
              <Pane marginY={majorScale(2)} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <img src={inbox} alt="No notifications" height="48px" />
                <Text marginTop={majorScale(2)}>No new notifications</Text>
              </Pane>
            )}

            {notifications.length > 0 && (
              <Pane>
                {notifications.map((n) => (
                  <NotificationItem key={n.id} notification={n} socket={socket} />
                ))}
                <Pane display="flex" alignItems="center" justifyContent="center" paddingY={majorScale(1)}>
                  <Button
                    disabled={loading || notifications.length < 1 || !metadata.has_next_page}
                    onClick={(e) => handleNextPage(e)}>
                    Load more...
                  </Button>
                </Pane>
              </Pane>
            )}
          </Pane>
        )}>
        <Pane>
          <IconButton icon={NotificationsIcon} appearance="minimal" intent={totalUnread > 0 ? 'danger' : 'none'} zIndex="1" />
          {totalUnread > 0 && (
            <Pill color="red" marginLeft="-16px" marginTop="-16px" zIndex="2" position="relative">{totalUnread}</Pill>
          )}
        </Pane>
      </Popover>
    </Pane>
  );
};

const NotificationItem = ({ notification, socket }) => {
  const handleClick = () => {
    socket.current.emit('mark_as_read', notification.id);
    if (notification.link) {
      window.location.href = notification.link;
    }
  };

  return (
    <Pane
      paddingX={majorScale(2)}
      paddingY={majorScale(1)}
      display="flex"
      alignItems="flex-start"
      hoverElevation={1}
      activeElevation={0}
      cursor="pointer"
      backgroundColor={notification.seen ? 'white' : '#EDEFF5'}
      onClick={handleClick}>
      <Pane
        height="32px"
        width="32px"
        backgroundColor="#E3E9FF"
        borderRadius="50%"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <NotificationsIcon color="info" />
      </Pane>
      <Pane marginLeft={majorScale(2)} width="100%">
        <Heading size={400}>{notification.title}</Heading>
        <Text
          maxWidth="240px"
          overflow="hidden"
          whiteSpace="normal"
          textOverflow="ellipsis"
          display="-webkit-box"
          style={{ display: '-webkit-box', WebkitLineClamp: '10', WebkitBoxOrient: 'vertical' }}>
          {notification.description}
        </Text>
        <Pane display="flex" justifyContent="flex-end">
          <Text color="muted" size={300}>{notification.created_at}</Text>
        </Pane>
      </Pane>
    </Pane>
  );
};

NotificationItem.propTypes = {
  notification: {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    link: PropTypes.string,
    seen: PropTypes.bool.isRequired,
    created_at: PropTypes.string.isRequired,
  },
  // eslint-disable-next-line react/forbid-prop-types
  socket: PropTypes.any,
};

NotificationItem.defaultProps = {
  notification: {
    link: null,
  },
  socket: null,
};

export default NotificationsMenu;
